import React, { useState, useEffect } from "react";
import { User, Mail, Lock, CheckCircle2 } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import toast from "react-hot-toast";

const Register = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", password: "", confirmpassword: "", gender: "male" });
  const [otp, setOtp] = useState("");

  useEffect(() => {
    if (Cookies.get("accessToken") && Cookies.get("refreshToken")) navigate("/");
  }, [navigate]);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmpassword) {
      toast.error("Passwords do not match!");
      return;
    }
    setIsSubmitting(true);
    
    try {
      const res = await fetch("https://orchestration.trackdsa.in/user/register", {
        method: "POST",
        body: JSON.stringify(formData),
        headers: { "Content-type": "application/json" },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Registration failed");
      
      Cookies.set("sessionId", data.authToken, { expires: 7 });
      setCurrentStep(1);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyOtp = async () => {
    setIsSubmitting(true);
    try {
      const res = await fetch("https://orchestration.trackdsa.in/user/verifyotp", {
        method: "POST",
        body: JSON.stringify({ sessionId: Cookies.get("sessionId"), otp }),
        headers: { "Content-type": "application/json" },
      });
      const data = await res.json();
      if (!res.ok || data.sessionId === false) throw new Error("Invalid OTP");

      Cookies.remove("sessionId");
      Cookies.set("accessToken", data.accessToken, { expires: 7 });
      Cookies.set("refreshToken", data.jwtRefreshToken, { expires: 7 });
      Cookies.set("profileName", data.name, { expires: 7 });
      
      toast.success("Registered Successfully!");
      navigate("/problems");
    } catch (err) {
      toast.error("Invalid OTP. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-xl p-8">
        
        {/* Progress Stepper */}
        <div className="flex items-center gap-4 mb-8">
          <div className={`h-2 flex-1 rounded-full ${currentStep >= 0 ? 'bg-blue-600' : 'bg-slate-200'}`} />
          <div className={`h-2 flex-1 rounded-full ${currentStep >= 1 ? 'bg-blue-600' : 'bg-slate-200'}`} />
        </div>

        {currentStep === 0 ? (
          <form onSubmit={handleRegister} className="space-y-4">
            <h2 className="text-2xl font-bold text-slate-800 mb-6">Create Account</h2>
            
            <div className="relative">
              <User className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
              <input type="text" required placeholder="Full Name" className="w-full pl-10 py-2.5 bg-slate-50 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500" onChange={(e) => setFormData({...formData, name: e.target.value})} />
            </div>

            <div className="relative">
              <Mail className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
              <input type="email" required placeholder="Email Address" className="w-full pl-10 py-2.5 bg-slate-50 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500" onChange={(e) => setFormData({...formData, email: e.target.value})} />
            </div>

            <div className="relative">
              <Lock className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
              <input type="password" required placeholder="Password" className="w-full pl-10 py-2.5 bg-slate-50 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500" onChange={(e) => setFormData({...formData, password: e.target.value})} />
            </div>

            <div className="relative">
              <Lock className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
              <input type="password" required placeholder="Confirm Password" className="w-full pl-10 py-2.5 bg-slate-50 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500" onChange={(e) => setFormData({...formData, confirmpassword: e.target.value})} />
            </div>

            <select className="w-full px-4 py-2.5 bg-slate-50 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500" onChange={(e) => setFormData({...formData, gender: e.target.value})}>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>

            <button type="submit" disabled={isSubmitting} className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition-all">
              {isSubmitting ? "Sending OTP..." : "Send OTP"}
            </button>
            <p className="text-center text-sm">Already have an account? <Link to="/login" className="text-blue-600 font-bold">Login</Link></p>
          </form>
        ) : (
          <div className="space-y-6 text-center">
            <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto" />
            <h2 className="text-2xl font-bold">Verify OTP</h2>
            <p className="text-slate-500">Enter the 6-digit code sent to your email.</p>
            <input 
              type="text" maxLength={6} placeholder="000000" 
              className="w-full text-center text-2xl tracking-[1em] py-3 border rounded-lg bg-slate-50 focus:ring-2 focus:ring-blue-500 outline-none"
              onChange={(e) => setOtp(e.target.value)}
            />
            <button onClick={handleVerifyOtp} className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700">
              {isSubmitting ? "Verifying..." : "Verify & Complete"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Register;